/**
 * Comprovante de retirada na expedição — quem retirou, quando, o que levou
 * e a assinatura desenhada. Abre uma janela isolada com o layout de
 * impressão (não depende do CSS do app) e chama print().
 */
import { fmtQtd } from "../helpers";

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function dataBr(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

// Só data URL de imagem entra no <img>: nada de HTML vindo do servidor.
function imagemSegura(src) {
  return typeof src === "string" && /^data:image\/(png|jpe?g|webp);base64,/i.test(src) ? src : "";
}

/**
 * @param sol      detalhe da solicitação (numero, tipo, cliente_nome, num_pedido)
 * @param retirada {retirado_por_nome, retirado_por_documento, retirado_por_telefone,
 *                  retirado_em, entregue_por_nome, itens, completa, observacao,
 *                  assinatura?, foto?}
 */
export function imprimirComprovanteRetirada(sol, retirada) {
  const itens = (retirada.itens || [])
    .map(
      (it, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${esc(it.descricao)}</td>
        <td class="r">${esc(fmtQtd(it.quantidade))}</td>
      </tr>`,
    )
    .join("");
  const assinatura = imagemSegura(retirada.assinatura);
  const foto = imagemSegura(retirada.foto);

  const html = `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<title>Retirada ${esc(sol.numero)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; }
  .slip { width: 100%; max-width: 480px; margin: 0 auto; padding: 18px 16px; }
  .marca { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #5b5bf6; }
  h1 { font-size: 26px; margin: 2px 0 2px; letter-spacing: -0.5px; }
  .sub { font-size: 13px; color: #555; margin-bottom: 10px; }
  .meta { font-size: 13px; line-height: 1.6; border-top: 1px solid #ddd; border-bottom: 1px solid #ddd; padding: 8px 0; margin-bottom: 10px; }
  .meta b { display: inline-block; min-width: 96px; color: #444; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { padding: 6px 4px; border-bottom: 1px solid #eee; text-align: left; vertical-align: top; }
  th { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #666; }
  td.c, th.c { text-align: center; width: 28px; }
  td.r, th.r { text-align: right; white-space: nowrap; }
  .parcial { margin-top: 8px; font-size: 12px; color: #a15c00; }
  .ass { margin-top: 22px; }
  .ass .lbl { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #666; }
  .ass img { display: block; max-width: 100%; height: 110px; object-fit: contain; border-bottom: 1px solid #333; }
  .ass .quem { font-size: 13px; margin-top: 4px; }
  .foto img { display: block; max-width: 100%; max-height: 260px; margin-top: 14px; border: 1px solid #ddd; }
  .rodape { margin-top: 18px; font-size: 11px; color: #777; }
  @media print { .slip { padding: 6mm 4mm; } }
</style></head>
<body>
  <div class="slip">
    <div class="marca">PharuERP · Comprovante de retirada</div>
    <h1>${esc(sol.numero)}</h1>
    <div class="sub">${esc(sol.tipo || "")}${sol.cliente_nome ? ` · ${esc(sol.cliente_nome)}` : ""}${sol.num_pedido ? ` · Pedido ${esc(sol.num_pedido)}` : ""}</div>
    <div class="meta">
      <div><b>Retirado por</b> ${esc(retirada.retirado_por_nome)}${retirada.retirado_por_documento ? ` · ${esc(retirada.retirado_por_documento)}` : ""}</div>
      ${retirada.retirado_por_telefone ? `<div><b>Telefone</b> ${esc(retirada.retirado_por_telefone)}</div>` : ""}
      <div><b>Quando</b> ${esc(dataBr(retirada.retirado_em))}</div>
      <div><b>Entregue por</b> ${esc(retirada.entregue_por_nome || "—")}</div>
      ${retirada.observacao ? `<div><b>Observação</b> ${esc(retirada.observacao)}</div>` : ""}
    </div>
    <table>
      <thead><tr><th class="c">#</th><th>Item</th><th class="r">Qtd</th></tr></thead>
      <tbody>${itens || `<tr><td colspan="3">Sem itens discriminados.</td></tr>`}</tbody>
    </table>
    ${retirada.completa ? "" : `<div class="parcial">Retirada PARCIAL — ainda há saldo a retirar nesta solicitação.</div>`}
    <div class="ass">
      <div class="lbl">Assinatura de quem retirou</div>
      ${assinatura ? `<img src="${assinatura}" alt="Assinatura" />` : `<div style="height:110px;border-bottom:1px solid #333"></div>`}
      <div class="quem">${esc(retirada.retirado_por_nome)}</div>
    </div>
    ${foto ? `<div class="foto"><img src="${foto}" alt="Foto da retirada" /></div>` : ""}
    <div class="rodape">Registro eletrônico no PharuERP em ${esc(dataBr(retirada.retirado_em))}.</div>
  </div>
  <script>window.addEventListener("load", function(){ setTimeout(function(){ window.print(); }, 150); });</script>
</body></html>`;

  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
